import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ServiceAnalytics() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = () => {
    if (!companyId) return;
    setLoading(true);
    let url = `${API}/companies/${companyId}/services/analytics`;
    const params = [];
    if (dateFrom) params.push(`date_from=${dateFrom}`);
    if (dateTo) params.push(`date_to=${dateTo}`);
    if (params.length) url += '?' + params.join('&');

    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [companyId]);

  if (initialLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (!data) return <div style={{ padding: 40 }}>No data available</div>;

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const fmtN = (v) => Number(v || 0).toLocaleString();

  const cardStyle = { background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: '1 1 200px', minWidth: 180 };
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const badgeStyle = (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: color + '20', color });

  const revenue = data.revenue || {};
  const cost = data.cost || {};
  const margin = data.margin || {};
  const labor = data.labor || {};
  const engagements = data.engagements || {};
  const clients = data.clients || {};
  const sales = data.sales || {};

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>📊 Service Analytics</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
          <span style={{ color: '#94a3b8' }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
          <button onClick={fetchData} style={{ padding: '6px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>Filter</button>
        </div>
      </div>

      {/* Revenue Cards */}
      <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, color: '#475569' }}>💰 Revenue</h3>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Expected</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>{fmt(revenue.expected)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Collected</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(revenue.collected)}</div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '4px solid #f59e0b' }}>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Outstanding</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>{fmt(revenue.outstanding)}</div>
        </div>
      </div>

      {/* Cost & Margin */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, color: '#475569' }}>📉 Cost</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ ...cardStyle, borderLeft: '4px solid #94a3b8' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Estimated</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{fmt(cost.estimated)}</div>
            </div>
            <div style={{ ...cardStyle, borderLeft: '4px solid #ef4444' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Actual</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(cost.actual)}</div>
            </div>
            <div style={{ ...cardStyle, borderLeft: '4px solid ' + (cost.variance > 0 ? '#ef4444' : '#22c55e') }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Variance</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: cost.variance > 0 ? '#ef4444' : '#22c55e' }}>{fmt(cost.variance)}</div>
            </div>
          </div>
        </div>
        <div>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, color: '#475569' }}>📈 Margin</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Amount</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: margin.amount >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(margin.amount)}</div>
            </div>
            <div style={{ ...cardStyle, borderLeft: '4px solid #6366f1' }}>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 4 }}>Percentage</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: margin.percentage >= 20 ? '#22c55e' : '#f59e0b' }}>{margin.percentage || 0}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Labor & Engagement Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>⏱️ Labor</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Estimated Hours</span><span style={{ fontWeight: 600 }}>{fmtN(labor.estimated_hours)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Actual Hours</span><span style={{ fontWeight: 600 }}>{fmtN(labor.actual_hours)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Revenue / Hour</span><span style={{ fontWeight: 600, color: '#22c55e' }}>{fmt(labor.revenue_per_hour)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Margin / Hour</span><span style={{ fontWeight: 600, color: '#8b5cf6' }}>{fmt(labor.margin_per_hour)}</span></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🤝 Engagements</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total</span><span style={{ fontWeight: 600 }}>{engagements.total || 0}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Active</span><span style={{ fontWeight: 600, color: '#22c55e' }}>{engagements.active || 0}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Completed</span><span style={{ fontWeight: 600, color: '#3b82f6' }}>{engagements.completed || 0}</span></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>👥 Clients</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total</span><span style={{ fontWeight: 600 }}>{clients.total || 0}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>New</span><span style={{ fontWeight: 600, color: '#22c55e' }}>{clients.new || 0}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Repeat</span><span style={{ fontWeight: 600, color: '#3b82f6' }}>{clients.repeat || 0}</span></div>
          </div>
        </div>
      </div>

      {/* Sales Win Rate */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🎯 Sales Performance</h3>
        <div style={{ display: 'flex', gap: 24 }}>
          <div><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Opportunities</span><div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{sales.total_opportunities || 0}</div></div>
          <div><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Won</span><div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#22c55e' }}>{sales.won || 0}</div></div>
          <div><span style={{ fontSize: '0.85rem', color: '#64748b' }}>Win Rate</span><div style={{ fontSize: '1.3rem', fontWeight: 700, color: sales.win_rate >= 50 ? '#22c55e' : '#f59e0b' }}>{sales.win_rate || 0}%</div></div>
        </div>
      </div>

      {/* Service Ranking */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>🏆 Service Ranking (by Revenue)</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Engagements</th>
                <th style={thStyle}>Clients</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Margin</th>
                <th style={thStyle}>Margin %</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.service_ranking) && data.service_ranking.map((s, i) => (
                <tr key={s.service_id}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={tdStyle}><span style={{ fontWeight: 600 }}>{s.service_name}</span><br /><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.service_code}</span></td>
                  <td style={tdStyle}>{s.engagement_count}</td>
                  <td style={tdStyle}>{s.client_count}</td>
                  <td style={tdStyle}>{fmt(s.total_revenue)}</td>
                  <td style={tdStyle}>{fmt(s.total_cost)}</td>
                  <td style={{ ...tdStyle, color: s.margin >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{fmt(s.margin)}</td>
                  <td style={tdStyle}><span style={badgeStyle(s.margin_pct >= 20 ? '#22c55e' : s.margin_pct >= 0 ? '#f59e0b' : '#ef4444')}>{s.margin_pct}%</span></td>
                </tr>
              ))}
              {(!data.service_ranking || data.service_ranking.length === 0) && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Category Roll-up */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📁 Category Roll-up</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Services</th>
                <th style={thStyle}>Engagements</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Margin</th>
                <th style={thStyle}>Labor Hrs</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.category_rollup) && data.category_rollup.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{c.services_count}</td>
                  <td style={tdStyle}>{c.engagements}</td>
                  <td style={tdStyle}>{fmt(c.collected_revenue)}</td>
                  <td style={tdStyle}>{fmt(c.actual_cost)}</td>
                  <td style={{ ...tdStyle, color: c.margin >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{fmt(c.margin)}</td>
                  <td style={tdStyle}>{fmtN(c.labor_hours)}</td>
                </tr>
              ))}
              {(!data.category_rollup || data.category_rollup.length === 0) && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue Trend */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📈 Monthly Revenue Trend (Last 12 Months)</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Month</th>
                <th style={thStyle}>Expected</th>
                <th style={thStyle}>Collected</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Engagements</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.revenue_trend) && data.revenue_trend.map(t => (
                <tr key={t.month}>
                  <td style={tdStyle}>{t.month}</td>
                  <td style={tdStyle}>{fmt(t.expected_revenue)}</td>
                  <td style={tdStyle}>{fmt(t.collected_revenue)}</td>
                  <td style={tdStyle}>{fmt(t.actual_cost)}</td>
                  <td style={tdStyle}>{t.engagements}</td>
                </tr>
              ))}
              {(!data.revenue_trend || data.revenue_trend.length === 0) && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No trend data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Owner Analysis */}
      <div style={sectionStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>👤 Owner Analysis</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>Engagements</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Cost</th>
                <th style={thStyle}>Margin</th>
                <th style={thStyle}>Labor Hrs</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.owner_analysis) && data.owner_analysis.map(o => (
                <tr key={o.service_owner_id}>
                  <td style={tdStyle}>{o.owner_name}</td>
                  <td style={tdStyle}>{o.engagement_count}</td>
                  <td style={tdStyle}>{fmt(o.total_revenue)}</td>
                  <td style={tdStyle}>{fmt(o.total_cost)}</td>
                  <td style={{ ...tdStyle, color: o.margin >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>{fmt(o.margin)}</td>
                  <td style={tdStyle}>{fmtN(o.labor_hours)}</td>
                </tr>
              ))}
              {(!data.owner_analysis || data.owner_analysis.length === 0) && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
